// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { LocationComponents } from '@/sparse/api/types';

export type AddressComponents = Omit<LocationComponents, 'formattedAddress'>;

/**
 * Finds the first address component of the given type.
 */
export function findComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string
): google.maps.GeocoderAddressComponent | undefined {
  return components.find((component) => component.types.includes(type));
}

// Google Places may return a city/town under different component types
// depending on the locality shape. For example, townships like
// "Georgetown Township" appear as administrative_area_level_3 rather
// than locality. This ordered list defines the fallback chain used to
// resolve the city/locality field.
const LOCALITY_TYPES = [
  'locality',
  'sublocality_level_1',
  'sublocality',
  'administrative_area_level_3',
  'administrative_area_level_2',
] as const;

function findLocality(
  components: google.maps.GeocoderAddressComponent[]
): string | null {
  for (const type of LOCALITY_TYPES) {
    const match = findComponent(components, type);
    if (match) {
      return match.long_name;
    }
  }
  return null;
}

/**
 * Extracts structured address fields from Google's address_components array.
 */
export function extractAddressComponents(
  components: google.maps.GeocoderAddressComponent[]
): AddressComponents {
  const streetNumber =
    findComponent(components, 'street_number')?.long_name ?? '';
  const route = findComponent(components, 'route')?.long_name ?? '';
  const subpremise = findComponent(components, 'subpremise')?.long_name ?? null;
  const locality = findLocality(components);
  const administrativeArea =
    findComponent(components, 'administrative_area_level_1')?.short_name ??
    null;
  const postalCode =
    findComponent(components, 'postal_code')?.long_name ?? null;
  const countryCode = findComponent(components, 'country')?.short_name ?? null;

  const line1 = [streetNumber, route].filter(Boolean).join(' ');

  return {
    addressLine1: line1 || null,
    addressLine2: subpremise,
    locality,
    administrativeArea,
    postalCode,
    countryCode,
  };
}

/**
 * Formats structured address components into a single address string.
 * Example: "123 Main St, Suite 200, Springfield, IL 62701, US"
 */
export function formatAddressFromComponents(
  components: AddressComponents
): string {
  const parts: string[] = [];

  if (components.addressLine1) {
    parts.push(components.addressLine1);
  }

  if (components.addressLine2) {
    parts.push(components.addressLine2);
  }

  if (components.locality) {
    parts.push(components.locality);
  }

  const stateZip = [components.administrativeArea, components.postalCode]
    .filter(Boolean)
    .join(' ');
  if (stateZip) {
    parts.push(stateZip);
  }

  if (components.countryCode) {
    parts.push(components.countryCode);
  }

  return parts.join(', ');
}
